import { type UserSearchItem } from '@atlaskit/smart-common';
import {
	type ActivityItemEdge,
	type RecommendedObject,
	type SearchPageNode,
} from '@atlassian/search-client';

import { type PrimaryProductKey, PrimaryProductKeys } from '../../../common/constants/products';
import type { HistoryItemData } from '../../../common/types';
import type { FilterSuggestionGroup } from '../../../common/ui/quick-find/types';
import { serveQFAppSelectorExperiment } from '../../../common/utils/experiments';

export type QuickFindResult<T> = {
	id: string;
	title: string;
	data: T;
	type: string;
};

export type RecentQueries = QuickFindResult<string>[];

export type RecentActivities = {
	result: QuickFindResult<ActivityItemEdge>[];
	duration: number;
};

export type BrowserHistoryActivities = {
	result: QuickFindResult<HistoryItemData>[];
};

export type UserDocuments = {
	result: QuickFindResult<RecommendedObject>[];
	duration: number;
};

export type SearchResults = {
	result: QuickFindResult<SearchPageNode>[];
	duration: number;
};

export type PeopleAndTeams = {
	result: QuickFindResult<UserSearchItem>[];
	duration: number;
};

export type ZeroQuery = QuickFindResult<string>;
export type ZeroQueries = { result: ZeroQuery[]; duration: number };

export type FilterSuggestionsResult = {
	result: QuickFindResult<FilterSuggestionGroup>[];
	duration: number;
};

/**
 * QuickFindContent contains query to ensure that the mapping between a set
 * of results and the query value is maintained. Query is used for analytics.
 */
export type QuickFindContent =
	| {
			state: 'PREQUERY_ERROR';
			query: '';
	  }
	| {
			state: 'PREQUERY_LOADING';
			query: '';
			recentQueries: QuickFindResult<string>[];
	  }
	| {
			state: 'PREQUERY_NO_RESULTS';
			query: '';
	  }
	| {
			state: 'PREQUERY_RESULTS';
			query: '';
			recentQueries: QuickFindResult<string>[];
			recentActivities: QuickFindResult<ActivityItemEdge>[];
			userDocuments: QuickFindResult<RecommendedObject>[];
			browserHistoryActivities: QuickFindResult<HistoryItemData>[];
			zeroQueries: QuickFindResult<string>[];
			filterSuggestions: QuickFindResult<FilterSuggestionGroup>[];
	  }
	| {
			state: 'POSTQUERY_ERROR';
			query: string;
	  }
	| {
			state: 'POSTQUERY_LOADING';
			query: string;
			recentQueries: QuickFindResult<string>[];
			recentActivities: QuickFindResult<ActivityItemEdge>[];
			browserHistoryActivities: QuickFindResult<HistoryItemData>[];
			userDocuments: QuickFindResult<RecommendedObject>[];
			zeroQueries: QuickFindResult<string>[];
			autocompleteSuggestions?: QuickFindResult<string>[];
	  }
	| {
			state: 'POSTQUERY_NO_RESULTS';
			query: string;
	  }
	| {
			state: 'POSTQUERY_RESULTS';
			query: string;
			recentQueries: QuickFindResult<string>[];
			recentActivities: QuickFindResult<ActivityItemEdge>[];
			browserHistoryActivities: QuickFindResult<HistoryItemData>[];
			userDocuments: QuickFindResult<RecommendedObject>[];
			searchResults: QuickFindResult<SearchPageNode>[];
			peopleAndTeams: QuickFindResult<UserSearchItem>[];
			zeroQueries: QuickFindResult<string>[];
			autocompleteSuggestions?: QuickFindResult<string>[];
	  }
	| undefined;

type StateHandlerArgs = {
	query?: string;
	hasFiltersSelected: boolean;
	hasQueryOrFilters: boolean;
	quickFindOpen: boolean;
	preQueryError?: Error;
	preQueryLoading: boolean;
	postQueryError?: Error;
	postQueryLoading: boolean;
	filteredRecentQueries: RecentQueries;
	filteredRecentActivities: RecentActivities;
	filteredUserDocuments: UserDocuments;
	filteredBrowserHistoryActivities: BrowserHistoryActivities;
	filteredZeroQueries: ZeroQuery[];
	searchResults: SearchResults;
	peopleAndTeams: PeopleAndTeams;
	postQueryDisabled?: boolean;
	primaryProduct?: PrimaryProductKey;
	hasProductSelected?: boolean;
	autocompleteSuggestions?: string[];
	filterSuggestions: FilterSuggestionsResult;
};

type QueryArgs = {
	query?: string;
	hasFiltersSelected: boolean;
};

/**
 * Function to determine if there are search parameters present. This is defined
 * by a query term or selected filters or app selected as being part of the search query.
 */
export const containsSearchParameters = ({ query, hasFiltersSelected }: QueryArgs) => {
	return Boolean(query || hasFiltersSelected);
};

const preQueryErrorHandler = ({
	hasQueryOrFilters,
	preQueryError,
	postQueryDisabled,
}: StateHandlerArgs): QuickFindContent | undefined => {
	if ((!hasQueryOrFilters || postQueryDisabled) && preQueryError) {
		return {
			state: 'PREQUERY_ERROR',
			query: '',
		};
	}
};

const preQueryLoadingHandler = ({
	hasQueryOrFilters,
	preQueryLoading,
	filteredRecentQueries,
	postQueryDisabled,
}: StateHandlerArgs): QuickFindContent | undefined => {
	if ((!hasQueryOrFilters || postQueryDisabled) && preQueryLoading) {
		return {
			state: 'PREQUERY_LOADING',
			query: '',
			recentQueries: filteredRecentQueries,
		};
	}
};

const preQueryNoResultsHandler = ({
	hasQueryOrFilters,
	filteredRecentActivities,
	filteredUserDocuments,
	filteredBrowserHistoryActivities,
	filteredZeroQueries,
	postQueryDisabled,
}: StateHandlerArgs): QuickFindContent | undefined => {
	const totalResults =
		filteredRecentActivities.result.length +
		filteredUserDocuments.result.length +
		filteredBrowserHistoryActivities.result.length +
		filteredZeroQueries.length;

	if ((!hasQueryOrFilters || postQueryDisabled) && totalResults === 0) {
		return {
			state: 'PREQUERY_NO_RESULTS',
			query: '',
		};
	}
};

const preQueryResultsHandler = ({
	hasQueryOrFilters,
	filteredRecentQueries,
	filteredRecentActivities,
	filteredUserDocuments,
	filteredBrowserHistoryActivities,
	filteredZeroQueries,
	postQueryDisabled,
	filterSuggestions,
}: StateHandlerArgs): QuickFindContent | undefined => {
	const totalResults =
		filteredRecentActivities.result.length +
		filteredUserDocuments.result.length +
		filteredBrowserHistoryActivities.result.length +
		filteredZeroQueries.length;

	if ((!hasQueryOrFilters || postQueryDisabled) && totalResults > 0) {
		return {
			state: 'PREQUERY_RESULTS',
			query: '',
			recentQueries: filteredRecentQueries,
			recentActivities: filteredRecentActivities.result,
			userDocuments: filteredUserDocuments.result,
			browserHistoryActivities: filteredBrowserHistoryActivities.result,
			zeroQueries: filteredZeroQueries,
			filterSuggestions: filterSuggestions.result,
		};
	}
};

const postQueryErrorHandler = ({
	query,
	hasQueryOrFilters,
	postQueryError,
	postQueryDisabled,
}: StateHandlerArgs): QuickFindContent | undefined => {
	if (hasQueryOrFilters && !postQueryDisabled && postQueryError) {
		return {
			state: 'POSTQUERY_ERROR',
			query: query || '',
		};
	}
};

const postQueryLoadingHandler = ({
	query,
	hasQueryOrFilters,
	postQueryLoading,
	filteredRecentActivities,
	filteredUserDocuments,
	filteredBrowserHistoryActivities,
	filteredRecentQueries,
	filteredZeroQueries,
	postQueryDisabled,
	primaryProduct,
	autocompleteSuggestions,
}: StateHandlerArgs): QuickFindContent | undefined => {
	if (hasQueryOrFilters && !postQueryDisabled && postQueryLoading) {
		// zero queries are only relevant for Jira in post query loading state
		const zeroQueries = primaryProduct === PrimaryProductKeys.Jira ? filteredZeroQueries : [];

		return {
			state: 'POSTQUERY_LOADING',
			query: query || '',
			recentActivities: filteredRecentActivities.result,
			userDocuments: filteredUserDocuments.result,
			browserHistoryActivities: filteredBrowserHistoryActivities.result,
			recentQueries: filteredRecentQueries,
			zeroQueries,
			autocompleteSuggestions: autocompleteSuggestions?.map((suggestion, index) => ({
				id: `autocomplete-suggestion-${index}`,
				title: suggestion,
				data: suggestion,
				type: 'autocomplete-suggestion',
			})),
		};
	}
};

const postQueryNoResultsHandler = ({
	query,
	hasQueryOrFilters,
	filteredRecentActivities,
	filteredUserDocuments,
	filteredBrowserHistoryActivities,
	searchResults,
	peopleAndTeams,
	postQueryDisabled,
}: StateHandlerArgs): QuickFindContent | undefined => {
	const totalResults =
		filteredRecentActivities.result.length +
		filteredUserDocuments.result.length +
		filteredBrowserHistoryActivities.result.length +
		searchResults.result.length +
		peopleAndTeams.result.length;

	if (hasQueryOrFilters && !postQueryDisabled && totalResults === 0) {
		return {
			state: 'POSTQUERY_NO_RESULTS',
			query: query || '',
		};
	}
};

const postQueryResultsHandler = ({
	query,
	hasQueryOrFilters,
	filteredRecentQueries,
	filteredRecentActivities,
	filteredUserDocuments,
	filteredBrowserHistoryActivities,
	searchResults,
	peopleAndTeams,
	postQueryDisabled,
	filteredZeroQueries,
	primaryProduct,
	autocompleteSuggestions,
}: StateHandlerArgs): QuickFindContent | undefined => {
	// zero queries are only relevant for Jira in post query results state
	const zeroQueries = primaryProduct === PrimaryProductKeys.Jira ? filteredZeroQueries : [];

	const totalResults =
		filteredRecentActivities.result.length +
		filteredUserDocuments.result.length +
		filteredBrowserHistoryActivities.result.length +
		searchResults.result.length +
		peopleAndTeams.result.length +
		zeroQueries.length;

	if (hasQueryOrFilters && !postQueryDisabled && totalResults > 0) {
		return {
			state: 'POSTQUERY_RESULTS',
			query: query || '',
			recentQueries: filteredRecentQueries,
			recentActivities: filteredRecentActivities.result,
			userDocuments: filteredUserDocuments.result,
			searchResults: searchResults.result,
			peopleAndTeams: peopleAndTeams.result,
			browserHistoryActivities: filteredBrowserHistoryActivities.result,
			zeroQueries,
			autocompleteSuggestions: autocompleteSuggestions?.map((suggestion, index) => ({
				id: `autocomplete-suggestion-${index}`,
				title: suggestion,
				data: suggestion,
				type: 'autocomplete-suggestion',
			})),
		};
	}
};

export const getQuickFindContent = (
	args: Omit<StateHandlerArgs, 'hasQueryOrFilters'>,
): QuickFindContent => {
	if (!args.quickFindOpen) {
		return;
	}

	const serveQFAppSelector = serveQFAppSelectorExperiment(args.primaryProduct, {
		fireExperimentExposure: false,
	});

	const hasProductSelected = (serveQFAppSelector && args.hasProductSelected) ?? false;
	const hasQueryOrFilters = containsSearchParameters({
		query: args.query,
		hasFiltersSelected: args.hasFiltersSelected || hasProductSelected,
	});

	const handlers = [
		preQueryErrorHandler,
		preQueryLoadingHandler,
		preQueryNoResultsHandler,
		preQueryResultsHandler,
		postQueryErrorHandler,
		postQueryLoadingHandler,
		postQueryNoResultsHandler,
		postQueryResultsHandler,
	];

	for (const handler of handlers) {
		const result = handler({ ...args, hasQueryOrFilters });

		if (result) {
			return result;
		}
	}

	// This should never happen, but default to error state
	if (!args.query) {
		return {
			state: 'PREQUERY_ERROR',
			query: '',
		};
	} else {
		return {
			state: 'POSTQUERY_ERROR',
			query: args.query,
		};
	}
};
